import { t } from "@airange/shared/i18n-admin-controls";
import type { MenuProps } from "antd";
import { App, Button, Dropdown, Space, Typography } from "antd";
import { openAdminConfirm } from "./admin-action-confirm";
import { AdminActionIcon, renderAdminActionIcon } from "./admin-action-icons";
import type { AdminBatchMenuAction } from "./admin-action-types";

type AdminBatchActionsProps = {
  selectedCount: number;
  onSelectCurrentPage: () => void;
  onSelectFiltered?: (() => void) | undefined;
  selectFilteredPending?: boolean;
  onClearSelection: () => void;
  menuActions: AdminBatchMenuAction[];
};

export function AdminBatchActions({
  selectedCount,
  onSelectCurrentPage,
  onSelectFiltered,
  selectFilteredPending,
  onClearSelection,
  menuActions,
}: AdminBatchActionsProps) {
  const { modal } = App.useApp();
  const items: NonNullable<MenuProps["items"]> = menuActions
    .filter((action) => !action.hidden)
    .map((action) => ({
      key: action.key,
      label: action.label,
      icon: renderAdminActionIcon(action),
      danger: Boolean(action.danger),
      disabled: Boolean(action.disabled || action.loading),
      onClick: () => {
        if (action.confirm) {
          openAdminConfirm(modal, {
            title: action.confirm.title,
            content: action.confirm.content,
            okText: action.confirm.okText,
            okDanger: action.confirm.okDanger,
            onOk: action.onClick,
          });
          return;
        }
        action.onClick?.();
      },
    }));

  return (
    <Space size={[8, 8]} wrap>
      <Typography.Text type="secondary">
        {t("common.messages.selectedCount", { count: selectedCount })}
      </Typography.Text>
      <Button
        size="small"
        icon={<AdminActionIcon name="CheckSquareOutlined" />}
        onClick={onSelectCurrentPage}
      >
        {t("common.actions.selectCurrentPage")}
      </Button>
      {onSelectFiltered ? (
        <Button
          size="small"
          icon={<AdminActionIcon name="SelectOutlined" />}
          loading={Boolean(selectFilteredPending)}
          onClick={onSelectFiltered}
        >
          {t("common.actions.selectAcrossPagesMax")}
        </Button>
      ) : null}
      {selectedCount > 0 ? (
        <Button
          size="small"
          icon={<AdminActionIcon name="ClearOutlined" />}
          onClick={onClearSelection}
        >
          {t("common.actions.clearSelection")}
        </Button>
      ) : null}
      {items.length > 0 && selectedCount > 0 ? (
        <Dropdown menu={{ items }} trigger={["click"]}>
          <Button
            color="primary"
            variant="solid"
            size="small"
            icon={<AdminActionIcon name="ThunderboltOutlined" />}
          >
            {t("common.actions.batchActions")}
          </Button>
        </Dropdown>
      ) : null}
    </Space>
  );
}
